/**
 * FASE 22.9 / 23.5 — SAFE PROACTIVE RESOLVER
 *
 * Non-invasive hints and banners only.
 * Includes session-level cooldowns, banner decay, and fatigue detection.
 * Never auto-confirms, never proposes mutations, never emits RT_EVENTS.
 */

import { getActiveWorkflowCount } from "./workflowSelectors";
import { getUrgentWorkflows }     from "./workflowPriorities";
import { getOverdueWorkflows }    from "./followupEngine";
import { generateProactiveInsights } from "./proactiveInsights";
import { resolveNextActions }     from "./resolveNextActions";
import { computeFollowupItems }   from "./followupEngine";

/* ─── Session-level fatigue tracking ─────────────────────────── */

/** How many times we've surfaced an info-level banner this session */
let _infoBannerCount   = 0;
/** Timestamp of last banner of any level */
let _lastBannerTs      = 0;
const INFO_FATIGUE_MAX = 2;        // stop showing info banners after this many times
const BANNER_QUIET_MS  = 4 * 60 * 1000; // 4 min between same-level banners

/**
 * @typedef {Object} ProactiveHints
 * @property {{ level: "urgent"|"attention"|"info", text: string }|null} priorityBanner
 * @property {NextAction[]} nextActionItems — max 3
 * @property {string|null}  insightText
 * @property {FollowupItem[]} followupItems
 */

/**
 * Returns display-safe objects for banners, next-action bars, and insight text.
 * Applies session-level fatigue detection and cooldowns to avoid noise.
 * @param {{ entityContext?: object, appScope?: object }} [opts]
 * @returns {ProactiveHints}
 */
export function getSafeProactiveHints({ entityContext = null, appScope = null } = {}) {
  const overdue  = getOverdueWorkflows();
  const urgent   = getUrgentWorkflows();
  const count    = getActiveWorkflowCount();
  const items    = computeFollowupItems();
  const now      = Date.now();

  /* ── Priority banner ─────────────────────────────────────── */
  let priorityBanner = null;

  if (overdue.length > 0) {
    // Urgent: always show, never suppressed
    priorityBanner = {
      level: "urgent",
      text:  overdue.length === 1
        ? "Hay un proceso que llevas un tiempo sin atender."
        : `Hay ${overdue.length} procesos que llevan tiempo sin actividad.`,
    };
    _lastBannerTs = now;
  } else if (urgent.length > 0) {
    // Attention: show unless very recently shown
    const recentlySeen = now - _lastBannerTs < BANNER_QUIET_MS;
    if (!recentlySeen) {
      priorityBanner = {
        level: "attention",
        text:  urgent.length === 1
          ? "Uno de tus procesos pastorales merece un poco de atención."
          : `Tienes ${urgent.length} procesos que podrían avanzar pronto.`,
      };
      _lastBannerTs = now;
    }
  } else if (count > 0 && _infoBannerCount < INFO_FATIGUE_MAX) {
    // Info: show only a limited number of times per session
    const recentlySeen = now - _lastBannerTs < BANNER_QUIET_MS;
    if (!recentlySeen) {
      priorityBanner = {
        level: "info",
        text:  count === 1
          ? "Tienes un proceso pastoral activo."
          : `Tienes ${count} procesos pastorales activos.`,
      };
      _infoBannerCount++;
      _lastBannerTs = now;
    }
  }

  // Next action items — max 3 concrete workflow steps
  const nextActionItems = resolveNextActions({ entityContext, appScope });

  // Insight text — max 1, pastoral tone
  const insights    = generateProactiveInsights(null, 1);
  const insightText = insights[0]?.text ?? null;

  return { priorityBanner, nextActionItems, insightText, followupItems: items };
}

/**
 * Returns true only when there's something genuinely worth surfacing.
 * Conservative — only urgent/attention, not info.
 */
export function shouldShowProactiveBanner() {
  return getOverdueWorkflows().length > 0 || getUrgentWorkflows().length > 0;
}

/** Reset session fatigue counters (call on sign-out or new session). */
export function resetProactiveFatigue() {
  _infoBannerCount = 0;
  _lastBannerTs    = 0;
}
